import { jsx as _jsx } from "react/jsx-runtime";
import { memo } from "react";
import { useTranslation } from "react-i18next";
import { hasDecisionRules } from "@bliks-io/contracts";
import { ProcessFlowStepDecisionRules, ProcessFlowStepDetailContent } from "../step-detail";
import { ChatEntityViewStateNotice, ChatProcessRecordCard } from "./chat-entity-view-shell";
/**
 * The card one recorded step is drawn in, whichever part of it a view shows:
 * the fetch, the three non-ready states and the header the two views would
 * otherwise state identically. Each view supplies only its body.
 */ function StepRecordCard({ data, processId, stepRecordingId, slug, children }) {
    const { t } = useTranslation();
    const state = data.useStepInProcess(processId, stepRecordingId);
    if (state.status !== "ready") return /*#__PURE__*/ _jsx(ChatEntityViewStateNotice, {
        state: state
    });
    return /*#__PURE__*/ _jsx(ChatProcessRecordCard, {
        data: data,
        name: state.step.generatedName || t("chat.entityView.untitledStep"),
        updatedAt: state.step.updatedAt,
        processId: processId,
        slug: slug,
        children: children(state)
    });
}
/**
 * One recorded step, drawn by the same component the process page's sidebar
 * uses.
 *
 * Memoised on its ids: once the tool part lands the assistant usually keeps
 * emitting text, so this stays mounted through the streaming turn's throttled
 * frames. Taking ids rather than the parsed payload is what makes that work —
 * zod hands back a fresh object per parse.
 */ export const ProcessStepView = /*#__PURE__*/ memo(function ProcessStepView({ data, processId, stepRecordingId, slug }) {
    return /*#__PURE__*/ _jsx(StepRecordCard, {
        data: data,
        processId: processId,
        stepRecordingId: stepRecordingId,
        slug: slug,
        children: (resolved)=>/*#__PURE__*/ _jsx(ProcessFlowStepDetailContent, {
                step: resolved.step,
                showSensitiveFields: resolved.canViewSensitiveFields,
                showVariantCheck: resolved.canViewProcessInsights,
                assigneeLookup: resolved.assigneeLookup,
                entitySuggestions: resolved.entitySuggestions
            })
    });
});
/**
 * One step's decision table on its own, drawn by the step detail's sibling
 * component (plan 016 §5 states why it is a view of its own).
 *
 * Neither capability is threaded; this view draws no gated section.
 */ export const StepDecisionRulesView = /*#__PURE__*/ memo(function StepDecisionRulesView({ data, processId, stepRecordingId, slug }) {
    const { t } = useTranslation();
    return /*#__PURE__*/ _jsx(StepRecordCard, {
        data: data,
        processId: processId,
        stepRecordingId: stepRecordingId,
        slug: slug,
        children: (resolved)=>// Asking first is what keeps the section's own render-nothing branch
            // unreachable — a card with a heading over a blank reads as broken. The
            // tool refuses a tableless step at answer time (`NO_DECISION_RULES`), so
            // the other arm means the step lost its table afterwards, and says so
            // without claiming the step itself is gone.
            hasDecisionRules(resolved.step) ? /*#__PURE__*/ _jsx(ProcessFlowStepDecisionRules, {
                step: resolved.step,
                assigneeLookup: resolved.assigneeLookup,
                entitySuggestions: resolved.entitySuggestions
            }) : /*#__PURE__*/ _jsx("p", {
                className: "text-sm text-muted-foreground",
                children: t("chat.entityView.noDecisionRules")
            })
    });
});

//# sourceMappingURL=chat-step-views.js.map